import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AditionContext } from '../../../components/Advertising/Adition/AditionContext.js';
import { encodeProfileValue } from '../../../components/Advertising/Adition/utils.js';
import canUseDOM from '../../../utils/canUseDOM.js';
export const API_URL = 'https://imagesrv.adition.com/js/srp.js';
export const AditionProvider = ({
  enabled = true,
  slots,
  profiles: profilesProp = {},
  children
}) => {
  const aditionGlobalSetup = useRef(false);
  const registeredSlotIds = useRef([]);
  const renderTimerId = useRef(0);
  // duplicated in a ref so the api setup callback below reads fresh values
  // @FIXME find a way to make profiles reactive instead (ie. reload ads if profiles change)
  const profiles = useRef(null);
  if (canUseDOM && aditionGlobalSetup.current === false) {
    aditionGlobalSetup.current = true;
    window.adition = window.adition || {};
    window.adition.srq = window.adition.srq || [];
    window.adition.srq.push(api => {
      api.registerAdfarm('ad1.adfarm1.adition.com');
      api.modules.setLoadingTimeout(5000);
      // Configure slots
      for (const key in slots) {
        const slot = slots[key];
        api.configureRenderSlot(slot.id).setContentunitId(slot.contentUnitId);
      }
      // Configure profiles
      for (const key in profiles.current) {
        const value = profiles.current[key];
        api.setProfile(key, encodeProfileValue(value));
      }
    });
  }
  const getSlot = useCallback(id => slots[id], [slots]);
  const registerSlot = useCallback(slotId => {
    if (!canUseDOM) {
      return;
    }
    const alreadyRegistered = registeredSlotIds.current.some(registeredSlotId => registeredSlotId === slotId);
    if (!alreadyRegistered) {
      registeredSlotIds.current.push(slotId);
    }
    window.clearTimeout(renderTimerId.current);
    renderTimerId.current = window.setTimeout(() => {
      window.adition.srq.push(api => {
        registeredSlotIds.current.forEach(slotId => {
          api.renderSlot(slotId);
        });
        api.load(registeredSlotIds.current).completeRendering(registeredSlotIds.current);
      });
    }, 0);
  }, []);
  const deregisterSlot = useCallback(slotId => {
    if (!canUseDOM) {
      return;
    }
    registeredSlotIds.current = registeredSlotIds.current.filter(registeredSlotId => registeredSlotId !== slotId);
    if (registeredSlotIds.current.length === 0) {
      window.clearTimeout(renderTimerId.current);
    }
  }, []);
  useEffect(() => {
    profiles.current = profilesProp;
  }, [profilesProp]);
  useEffect(() => {
    if (!canUseDOM || !enabled) {
      return;
    }
    const scripts = Array.from(document.getElementsByTagName('script'));
    // Skip if already added
    if (scripts.some(script => script.src.includes(API_URL))) {
      return;
    }
    // Load library
    const script = document.createElement('script');
    script.src = API_URL;
    script.async = true;
    document.head.appendChild(script);
  }, [enabled]);
  const contextValue = useMemo(() => ({
    enabled,
    getSlot,
    registerSlot,
    deregisterSlot
  }), [enabled, getSlot, registerSlot, deregisterSlot]);
  return _jsx(AditionContext.Provider, {
    value: contextValue,
    children: children
  });
};